import React from 'react';
import { Chip, Tooltip, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp as MarkupIcon,
  TrendingDown as DistIcon,
  TrendingFlat as AccumIcon,
  Compress as AbsorbIcon,
} from '@mui/icons-material';

const PHASE_CONFIG = {
  accumulation: {
    color: '#3b82f6',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    bgColor: 'rgba(59, 130, 246, 0.08)',
    borderColor: 'rgba(59, 130, 246, 0.2)',
    icon: AccumIcon,
  },
  markup: {
    color: '#10b981',
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    bgColor: 'rgba(16, 185, 129, 0.08)',
    borderColor: 'rgba(16, 185, 129, 0.2)',
    icon: MarkupIcon,
  },
  distribution: {
    color: '#ef4444',
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    bgColor: 'rgba(239, 68, 68, 0.08)',
    borderColor: 'rgba(239, 68, 68, 0.2)',
    icon: DistIcon,
  },
  absorption: {
    color: '#f59e0b',
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    bgColor: 'rgba(245, 158, 11, 0.08)',
    borderColor: 'rgba(245, 158, 11, 0.2)',
    icon: AbsorbIcon,
  },
};

export function getPhaseConfig(phase) {
  return PHASE_CONFIG[phase] || PHASE_CONFIG.accumulation;
}

export default function PhaseChip({ phase, confidence, size = 'medium', showTooltip = true }) {
  const { t } = useTranslation();
  const config = getPhaseConfig(phase);
  const Icon = config.icon;

  const chip = (
    <Chip
      icon={<Icon sx={{ fontSize: size === 'small' ? 13 : 15, color: `${config.color} !important` }} />}
      label={
        <Typography component="span" sx={{
          fontSize: size === 'small' ? '0.68rem' : '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          lineHeight: 1,
        }}>
          {t(`phase.phases.${phase}`)}
        </Typography>
      }
      size={size}
      sx={{
        backgroundColor: config.bgColor,
        color: config.color,
        border: `1.5px solid ${config.borderColor}`,
        borderRadius: '8px',
        height: size === 'small' ? 26 : 30,
        '& .MuiChip-icon': { ml: '6px' },
        '& .MuiChip-label': { px: 1 },
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: `${config.color}15`,
          borderColor: `${config.color}60`,
          transform: 'translateY(-1px)',
        },
      }}
    />
  );

  if (!showTooltip) return chip;

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.3 }}>{t(`phase.phases.${phase}`)}</Typography>
          <Typography sx={{ fontSize: 11, opacity: 0.85 }}>{t(`phase.phaseDesc.${phase}`)}</Typography>
          {confidence != null && (
            <Typography sx={{ fontSize: 11, mt: 0.5, fontWeight: 600 }}>
              {t('phase.detail.confidence')}: {(confidence * 100).toFixed(0)}%
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      {chip}
    </Tooltip>
  );
}
